// Types
import type { ActionMenuConfig, ActionMenuResolverContext } from '@milanote/roma/application';
import type { MNHotInstance } from '../../../bridge/tableHotInstanceTypes';

// Utils
import { debugTableCells, debugTableInstance } from '../../utils/hotInstanceUtils/debug/hotTableDebugUtils';
import {
    addHotTableColsLeft,
    addHotTableColsRight,
    addHotTableRowsAbove,
    addHotTableRowsBelow,
    isHotTableColRemovalDisabled,
    isHotTableRowRemovalDisabled,
    removeHotTableCols,
    removeHotTableRows,
} from '../../utils/hotInstanceUtils/grid/hotTableGridOperationsUtils';
import { clearSelectedCells } from '../../utils/hotInstanceUtils/grid/hotTableGridShortcutsUtils';
import {
    isFirstSelectionRefColHeader,
    isFirstSelectionRefRowHeader,
    isSelectingSingleColumn,
    isSelectingSingleRow,
} from '../../../bridge/utils/selectionUtils/tableSelectionClassificationUtils';
import { isSelectingMultipleSelections } from '../../../bridge/utils/selectionUtils/tableSelectionPredicateUtils';
import {
    copyTableCellsFromContextMenu,
    cutTableCellsFromContextMenu,
    pasteTableCellsFromContextMenuV2,
    readTableClipboardText,
} from '../../../bridge/handlers/contextMenu/tableContextMenuClipboardUtils';
import type { TableContextMenuAction } from '../../../bridge/handlers/contextMenu/useTableContextMenuActions';

export type TableContextMenuV2Context = ActionMenuResolverContext & {
    locale: string;
    elementId: string;
    hotInstance: MNHotInstance;
    showDebugTools: boolean;
    /** Defers a grid mutation until one frame after the menu has closed and the grid is listening again. */
    runGridAction: (action: TableContextMenuAction) => void;
    runClipboardAction: (action: TableContextMenuAction) => void;
};

/** Selects the `_one`/`_other` locale variant from the current row selection, mirroring the built-in HOT menu. */
const rowLocaleKey = (baseKey: string) => (context: TableContextMenuV2Context) =>
    isSelectingSingleRow(context.hotInstance.getSelected()) ? `${baseKey}_one` : `${baseKey}_other`;

/** Selects the `_one`/`_other` locale variant from the current column selection, mirroring the built-in HOT menu. */
const columnLocaleKey = (baseKey: string) => (context: TableContextMenuV2Context) =>
    isSelectingSingleColumn(context.hotInstance.getSelected()) ? `${baseKey}_one` : `${baseKey}_other`;

/** Rows cannot be inserted from a column-header or multi-range selection. */
const isRowInsertDisabled = (context: TableContextMenuV2Context): boolean => {
    const cellSelections = context.hotInstance.getSelected();

    return isSelectingMultipleSelections(cellSelections) || isFirstSelectionRefColHeader(cellSelections);
};

/** Columns cannot be inserted from a row-header or multi-range selection. */
const isColumnInsertDisabled = (context: TableContextMenuV2Context): boolean => {
    const cellSelections = context.hotInstance.getSelected();

    return isSelectingMultipleSelections(cellSelections) || isFirstSelectionRefRowHeader(cellSelections);
};

/** Roma table context menu config, calling the same `hotInstanceUtils` functions as the HOT menu. */
export const tableContextMenuV2Config: ActionMenuConfig<TableContextMenuV2Context> = [
    {
        id: 'clipboard',
        items: [
            {
                type: 'action',
                id: 'cut',
                localeKey: 'cut',
                onAction: (context) => context.runClipboardAction(cutTableCellsFromContextMenu),
            },
            {
                type: 'action',
                id: 'copy',
                localeKey: 'copy',
                onAction: (context) => context.runClipboardAction(copyTableCellsFromContextMenu),
            },
            {
                type: 'action',
                id: 'paste',
                localeKey: 'paste',
                onAction: (context) => {
                    // Clipboard reads need the menu click's transient user activation, so start reading
                    // now and only defer applying the text to the grid.
                    const clipboardTextPromise = readTableClipboardText();

                    context.runGridAction((hotInstance) =>
                        pasteTableCellsFromContextMenuV2(hotInstance, clipboardTextPromise),
                    );
                },
            },
        ],
    },
    {
        id: 'rows',
        items: [
            {
                type: 'action',
                id: 'add-rows-above',
                localeKey: rowLocaleKey('addRowAbove'),
                shortcut: ['alt', '↑'],
                disabledPredicate: isRowInsertDisabled,
                onAction: (context) => context.runGridAction((hotInstance) => addHotTableRowsAbove(hotInstance)),
            },
            {
                type: 'action',
                id: 'add-rows-below',
                localeKey: rowLocaleKey('addRowBelow'),
                shortcut: ['alt', '↓'],
                disabledPredicate: isRowInsertDisabled,
                onAction: (context) => context.runGridAction((hotInstance) => addHotTableRowsBelow(hotInstance)),
            },
        ],
    },
    {
        id: 'columns',
        items: [
            {
                type: 'action',
                id: 'add-columns-left',
                localeKey: columnLocaleKey('addColumnLeft'),
                shortcut: ['alt', '←'],
                disabledPredicate: isColumnInsertDisabled,
                onAction: (context) => context.runGridAction((hotInstance) => addHotTableColsLeft(hotInstance)),
            },
            {
                type: 'action',
                id: 'add-columns-right',
                localeKey: columnLocaleKey('addColumnRight'),
                shortcut: ['alt', '→'],
                disabledPredicate: isColumnInsertDisabled,
                onAction: (context) => context.runGridAction((hotInstance) => addHotTableColsRight(hotInstance)),
            },
        ],
    },
    {
        id: 'removal',
        items: [
            {
                type: 'action',
                id: 'clear-cell',
                localeKey: 'clearCell',
                onAction: (context) => context.runGridAction(clearSelectedCells),
            },
            {
                type: 'action',
                id: 'remove-rows',
                localeKey: rowLocaleKey('removeRow'),
                disabledPredicate: (context) => isHotTableRowRemovalDisabled(context.hotInstance),
                onAction: (context) => context.runGridAction((hotInstance) => removeHotTableRows(hotInstance)),
            },
            {
                type: 'action',
                id: 'remove-columns',
                localeKey: columnLocaleKey('removeColumn'),
                disabledPredicate: (context) => isHotTableColRemovalDisabled(context.hotInstance),
                onAction: (context) => context.runGridAction((hotInstance) => removeHotTableCols(hotInstance)),
            },
        ],
    },
    {
        id: 'debug',
        items: [
            {
                type: 'action',
                id: 'debug-table-instance',
                label: () => 'Debug Table Instance',
                availabilityPredicate: (context) => context.showDebugTools,
                onAction: (context) => debugTableInstance(context.hotInstance),
            },
            {
                type: 'action',
                id: 'debug-cell',
                label: () => 'Debug Cell',
                availabilityPredicate: (context) => context.showDebugTools,
                onAction: (context) =>
                    debugTableCells(context.hotInstance, context.elementId, context.hotInstance.getSelected() || []),
            },
        ],
    },
];

const DESKTOP_TOOLBAR_EXCLUDED_SECTIONS = new Set(['clipboard']);
const TOOLBAR_EXCLUDED_ITEMS = new Set(['add-rows-below', 'add-columns-right']);

/** Removes insertion actions duplicated by the table toolbar while preserving dynamic item resolvers. */
const filterToolbarItems = (items: (typeof tableContextMenuV2Config)[number]['items']) => {
    if (typeof items === 'function') {
        return (context: TableContextMenuV2Context) =>
            items(context).filter(({ id }) => !TOOLBAR_EXCLUDED_ITEMS.has(id));
    }

    return items.filter(({ id }) => !TOOLBAR_EXCLUDED_ITEMS.has(id));
};

const tableToolbarMenuConfig = tableContextMenuV2Config.map((section) => ({
    ...section,
    items: filterToolbarItems(section.items),
}));

/** Removes clipboard actions and insertion actions already available directly from the desktop table toolbar. */
export const tableDesktopToolbarMenuConfig = tableToolbarMenuConfig.filter(
    ({ id }) => !DESKTOP_TOOLBAR_EXCLUDED_SECTIONS.has(id),
);

/** Keeps tablet clipboard actions while removing insertion actions already available directly from its toolbar. */
export const tableTabletToolbarMenuConfig = tableToolbarMenuConfig;
